import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Badge } from "@/components/ui/badge";
import { ChevronsUpDown } from "lucide-react";

interface EnumMultiSelectProps {
  enumValues: string[];
  selected: string[];
  onChange: (selected: string[]) => void;
}

/**
 * Multi-select dropdown for enum filter values.
 * Renders checkboxes inside a popover with selected count in the trigger.
 * @param props - The component props
 * @param props.enumValues - Available enum values to choose from
 * @param props.selected - Currently selected values
 * @param props.onChange - Callback when selection changes
 * @returns The multi-select popover component
 */
export default function EnumMultiSelect({
  enumValues,
  selected,
  onChange,
}: EnumMultiSelectProps) {
  /**
   * Toggles a value in the selected array.
   * @param value - The enum value to toggle
   */
  function toggleValue(value: string) {
    if (selected.includes(value)) {
      onChange(selected.filter((v) => v !== value));
    } else {
      onChange([...selected, value]);
    }
  }

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className="h-7 text-xs flex-1 justify-between font-normal"
        >
          {selected.length === 0 ? (
            <span className="text-muted-foreground">Select values...</span>
          ) : (
            <span className="flex items-center gap-1 truncate">
              <Badge
                variant="secondary"
                className="text-[10px] px-1.5 py-0 h-4"
              >
                {selected.length}
              </Badge>
              <span className="truncate">{selected.join(", ")}</span>
            </span>
          )}
          <ChevronsUpDown className="h-3 w-3 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-56 p-2" align="start">
        <div className="space-y-1 max-h-48 overflow-y-auto">
          {enumValues.map((value) => (
            <label
              key={value}
              className="flex items-center gap-2 rounded px-2 py-1 text-xs cursor-pointer hover:bg-accent"
            >
              <Checkbox
                checked={selected.includes(value)}
                onCheckedChange={() => toggleValue(value)}
                className="h-3.5 w-3.5"
              />
              {value}
            </label>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
